import { FC, useMemo, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Text } from '@connect-x-app/hybrid-ui';
import { COLORS } from '@connect-x-app/shared-ui';

import { Icon } from '../../icon';
import { useInputError } from '../hooks/useInputError';
import { InputWrapper, InputWrapperProps } from '../InputWrapper';

import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.scss';

type Props = InputWrapperProps & {
  maxDate?: Date;
  minDate?: Date;
  onChange: (newDate: Date | null, name: string) => void;
  selectedValue: Date | null;
};

const CalendarButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <label className="calendar-icon-wrapper">
      <Icon className="calendar-icon" colorName={COLORS.yuma.$01} name={'calendarOutline'} onClick={onClick} size={2.4} />
    </label>
  );
};

const ClearButton: FC<{ onClear: () => void; selectedValue: Date | null }> = ({ onClear, selectedValue }) => {
  if (selectedValue) {
    return <Icon className="clear-icon" colorName={COLORS.zulu.$08} name={'close'} onClick={onClear} size={2.4} />;
  }
  return <></>;
};

export const DatePicker: FC<Props> = ({ label, maxDate, minDate, onChange, selectedValue, ...wrapperProps }) => {
  const { name, validation } = wrapperProps;
  const { setDirty, showError } = useInputError(validation);
  const datePickerRef = useRef<ReactDatePicker>(null);

  const onClear = () => {
    onChange(null, name);
  };

  const onClickCalenderButton = () => {
    datePickerRef?.current?.setFocus();
    datePickerRef?.current?.setOpen(true);
  };

  const onChangeInternal = (value: Date | null) => {
    let date = null;
    if (value) {
      date = new Date(value);
      date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }
    onChange(date, name);
  };

  const localSelectedValue = useMemo(() => {
    let date = null;
    if (selectedValue) {
      date = new Date(selectedValue);
      date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    }
    return date;
  }, [selectedValue]);

  return (
    <InputWrapper {...wrapperProps} showError={showError}>
      <Text as="span" className="date-picker__label" color={COLORS.zulu.$06} size="XS" weight="bold">
        {label}
      </Text>
      <div className="date-picker__input">
        <ReactDatePicker
          calendarStartDay={1}
          className="date-picker"
          dateFormat={'yyyy-MM-dd'}
          maxDate={maxDate}
          minDate={minDate}
          onChange={(date: Date | null) => {
            onChangeInternal(date);
            setDirty();
          }}
          placeholderText="YYYY-MM-DD"
          ref={datePickerRef}
          selected={localSelectedValue}
        />
        <div className="date-picker__button">
          <ClearButton onClear={onClear} selectedValue={localSelectedValue} />
          <CalendarButton onClick={onClickCalenderButton} />
        </div>
      </div>
    </InputWrapper>
  );
};
